import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { SavedQueryListItem, SavedQueryPreviewResponse } from '@innermate/shared';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function SessionBadge({ item }: { item: SavedQueryListItem }) {
  // 세션이 soft-deleted — sessionExists 는 true 지만 별도 amber 배지로 구분.
  // 어드민은 감사 / 휴지통 의도로 계속 조회 가능 (orphan 정리 대상 아님).
  if (item.sessionDeletedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-400"
        title={`사용자가 ${formatDateTime(item.sessionDeletedAt)} 에 삭제`}
      >
        <span className="size-1.5 rounded-full bg-amber-500" />
        <span className="truncate max-w-[160px] line-through opacity-80">
          {item.sessionTitle ?? '삭제된 대화'}
        </span>
      </span>
    );
  }
  if (item.sessionExists) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
        <span className="size-1.5 rounded-full bg-emerald-500" />
        <span className="truncate max-w-[160px]" title={item.sessionTitle ?? ''}>
          {item.sessionTitle ?? '대화 있음'}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
      <span className="size-1.5 rounded-full bg-destructive" />
      만료된 대화 (고아)
    </span>
  );
}

function MetaRow({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 text-sm">
      <dt className="text-foreground-muted">{k}</dt>
      <dd className="break-all">{children}</dd>
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function PreviewResult({ preview }: { preview: SavedQueryPreviewResponse }) {
  return (
    <div className="space-y-2 pt-2">
      <div className="flex items-center gap-2 text-xs text-foreground-muted">
        <span>미리보기 결과</span>
        <span className="font-mono">· {preview.rowCount}행</span>
        {preview.truncated ? (
          <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-400 font-mono">
            1000행 이상 — 자름
          </span>
        ) : null}
        {preview.injectedLimit ? (
          <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-mono">
            LIMIT 자동 주입
          </span>
        ) : null}
        <span className="font-mono">· {preview.latencyMs}ms</span>
      </div>
      <div className="rounded border border-border bg-surface overflow-auto max-h-96">
        {preview.columns.length === 0 ? (
          <div className="p-3 text-xs text-foreground-muted">빈 결과</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-surface-muted/40 sticky top-0">
              <tr>
                {preview.columns.map((c) => (
                  <th
                    key={c}
                    className="px-2 py-1.5 text-left font-medium font-mono uppercase text-foreground-muted whitespace-nowrap"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row, ri) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: preview 결과 정적 렌더
                  key={ri}
                  className="border-t border-border"
                >
                  {row.map((cell, ci) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: column index 는 컬럼 순서와 일치
                      key={ci}
                      className="px-2 py-1 font-mono whitespace-nowrap align-top"
                      title={formatCell(cell)}
                    >
                      {formatCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * 삭제 mutation 시그니처 — 호출자(페이지) 가 자체 invalidation 컨텍스트로 mutation 을 만들고
 * 모달에 주입한다. ChatLog 처럼 모달만 띄우는 페이지는 mutation 의 onSuccess 에서 trace 재조회
 * 같은 다른 후처리를 자유롭게 연결.
 */
export type SavedQueryDeleteMut = ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof api.adminDeleteSavedQuery>>,
    unknown,
    { id: string; confirmActive: boolean }
  >
>;

/**
 * 저장된 쿼리 상세 모달 — SavedQueries 페이지의 메인 사용처이지만 ChatLog 등 외부에서도 동일하게
 * 띄울 수 있도록 추출. 부모는 item / onClose / onDeleted / deleteMut 만 주입.
 */
export function SavedQueryDetailModal({
  item,
  onClose,
  onDeleted,
  deleteMut,
}: {
  item: SavedQueryListItem;
  onClose: () => void;
  onDeleted: () => void;
  deleteMut: SavedQueryDeleteMut;
}) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState<
    | null
    | { kind: 'orphan' }
    | { kind: 'active'; sessionId: string | null; sessionTitle: string | null }
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SavedQueryPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewMut = useMutation({
    mutationFn: (id: string) => api.adminPreviewSavedQuery(id),
    onSuccess: (data) => {
      setPreview(data);
      setPreviewError(null);
    },
    onError: (err) => {
      setPreview(null);
      setPreviewError(err instanceof ApiError ? err.message : '미리보기 실행 실패');
    },
  });

  async function handleDeleteClick() {
    setError(null);
    if (!item.sessionExists) {
      setConfirmOpen({ kind: 'orphan' });
      return;
    }
    // 살아있는 세션 → 1차 시도: confirmActive 없이 → 서버가 active_session 응답 반환.
    try {
      const res = await deleteMut.mutateAsync({ id: item.id, confirmActive: false });
      if (res.ok) {
        onDeleted();
      } else {
        setConfirmOpen({
          kind: 'active',
          sessionId: res.sessionId,
          sessionTitle: res.sessionTitle,
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    }
  }

  async function confirmDelete(confirmActive: boolean) {
    setError(null);
    try {
      const res = await deleteMut.mutateAsync({ id: item.id, confirmActive });
      if (res.ok) {
        setConfirmOpen(null);
        onDeleted();
      } else {
        setError('세션 상태가 바뀌었습니다. 다시 시도해주세요.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="쿼리 상세"
        description={item.id}
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={!item.sessionExists || !item.sessionId}
                onClick={() => {
                  if (!item.sessionId) return;
                  navigate({
                    to: '/admin/chat-log',
                    search: {
                      sessionId: item.sessionId,
                      // saved_query 생성일 기준 -30일 ~ 해당일 범위로 세션 검색.
                      date: item.createdAt.slice(0, 10),
                    },
                  });
                }}
                title={item.sessionExists ? '대화 메시지 열람' : '이미 삭제된 대화입니다'}
              >
                이 대화 보기
              </Button>
              <Button
                variant="secondary"
                onClick={() => previewMut.mutate(item.id)}
                disabled={previewMut.isPending}
                title="1000건 상한 · 60초 timeout 으로 쿼리를 실행합니다"
              >
                {previewMut.isPending ? '실행 중…' : '미리보기 실행'}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                닫기
              </Button>
              <Button variant="danger" onClick={handleDeleteClick} disabled={deleteMut.isPending}>
                {deleteMut.isPending ? '삭제 중…' : '삭제'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <MetaRow k="유형">{item.kind.toUpperCase()}</MetaRow>
          <MetaRow k="소유자">
            {item.ownerDisplayName ?? '—'}{' '}
            <span className="text-foreground-muted">· {item.ownerEmail ?? ''}</span>
          </MetaRow>
          {item.kind === 'db' ? (
            <MetaRow k="데이터소스">
              {item.dataSourceName ?? '—'}{' '}
              <span className="text-xs text-foreground-muted font-mono">{item.dataSourceId}</span>
            </MetaRow>
          ) : (
            <MetaRow k="엑셀 파일">
              {item.fileName ?? '—'}{' '}
              <span className="text-xs text-foreground-muted font-mono">{item.fileId}</span>
            </MetaRow>
          )}
          <MetaRow k="세션">
            <SessionBadge item={item} />
            {item.sessionTitle ? (
              <span className="ml-2 text-foreground-muted">{item.sessionTitle}</span>
            ) : null}
          </MetaRow>
          <MetaRow k="생성">{formatDateTime(item.createdAt)}</MetaRow>
          <MetaRow k="마지막 실행">
            {item.lastUsedAt ? formatDateTime(item.lastUsedAt) : '미실행'}
            {' · '}
            {item.useCount}회
          </MetaRow>
          <div className="space-y-1.5 pt-2">
            <div className="flex items-center gap-2">
              <div className="text-xs text-foreground-muted">SQL 원문 (LLM 생성)</div>
              {item.lastScopeInjected && item.lastScopeInjected.length > 0 ? (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-success/10 text-success border border-success/30"
                  title="권한 룰 (data_source_table_scope_rules) 이 실행 시 자동 주입됨"
                >
                  권한 룰 자동 주입 {item.lastScopeInjected.length}건
                </span>
              ) : null}
            </div>
            <pre className="font-mono text-[12px] whitespace-pre-wrap break-all bg-surface-muted/60 rounded p-3 max-h-80 overflow-y-auto">
              {item.sql}
            </pre>
          </div>
          {item.lastScopeInjected && item.lastScopeInjected.length > 0 ? (
            <div className="space-y-1.5 pt-1">
              <div className="text-xs text-foreground-muted">주입된 WHERE 조건</div>
              <ul className="text-[12px] space-y-0.5 bg-surface-muted/40 rounded p-2 border border-success/20">
                {item.lastScopeInjected.map((p, i) => (
                  <li key={i} className="font-mono">
                    <span className="text-success">AND</span>{' '}
                    {p.schemaName ? `${p.schemaName}.` : ''}
                    {p.tableName}.<strong>{p.column}</strong> ={' '}
                    <span className="text-foreground">'{p.value}'</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {item.lastExecutedSql ? (
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center gap-2">
                <div className="text-xs text-foreground-muted">실제 실행된 쿼리</div>
                <span className="text-[10px] text-foreground-muted">
                  권한 룰 주입 + LIMIT 가 적용된 최종 SQL
                </span>
              </div>
              <pre className="font-mono text-[12px] whitespace-pre-wrap break-all bg-surface-muted/60 rounded p-3 max-h-80 overflow-y-auto border-l-2 border-success/40">
                {item.lastExecutedSql}
              </pre>
            </div>
          ) : null}
          {previewError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {previewError}
            </div>
          ) : null}
          {preview ? <PreviewResult preview={preview} /> : null}
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </div>
      </Modal>

      {confirmOpen?.kind === 'orphan' ? (
        <Modal
          open
          layer="high"
          onClose={() => setConfirmOpen(null)}
          title="고아 쿼리 삭제"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(null)}>
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => confirmDelete(false)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? '삭제 중…' : '삭제'}
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            원본 대화가 이미 삭제된 고아 쿼리입니다. 연결된 대시보드가 있어도 더 이상 실행되지
            않으며 복구할 수 없습니다.
          </p>
        </Modal>
      ) : null}

      {confirmOpen?.kind === 'active' ? (
        <Modal
          open
          layer="high"
          onClose={() => setConfirmOpen(null)}
          title="사용 중인 대화가 있습니다"
          description={confirmOpen.sessionTitle ?? confirmOpen.sessionId ?? ''}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(null)}>
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => confirmDelete(true)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? '삭제 중…' : '알면서도 삭제'}
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            이 쿼리를 사용 중인 대화가 아직 살아있습니다. 삭제하면 해당 대화의 대시보드와 쿼리
            재실행이 <strong>만료</strong> 상태가 됩니다. 사용자에게는 "관리자에 의해
            만료되었습니다" 라고 표시됩니다.
          </p>
          <p className="mt-2 text-sm">정말 삭제하시겠습니까?</p>
        </Modal>
      ) : null}
    </>
  );
}
